import React, {
	createContext,
	useContext,
	useState,
	useCallback,
	useMemo,
	ReactNode,
	useEffect,
} from 'react'

import { Group } from '@/core/api/entities/group'
import { Zone } from '@/core/api/entities/zone/zone'

interface ZoneProperties {
	name: string
	storeId: number
	flags: string[]
}

interface ZoneDraftState {
	originalGroups: Group[] | null
	currentGroups: Group[]
	addedGroups: number[]
	removedGroups: number[]
	originalProperties: ZoneProperties | null
	currentProperties: ZoneProperties | null
	hasChanges: boolean
	isSaving: boolean
}

interface ZoneDraftContextType extends ZoneDraftState {
	current: Group[]
	setGroups: (groups: Group[]) => void
	addGroups: (groupIds: number[], allGroups: Group[]) => void
	removeGroups: (groupIds: number[]) => void
	updateZoneProperties: (properties: Partial<ZoneProperties>) => void
	resetDraft: () => void
	updateWithFreshData: (zone: Zone) => void
	setSaving: (isSaving: boolean) => void
	getChanges: () => {
		addedGroups: number[]
		removedGroups: number[]
		propertiesChanged: boolean
		properties?: ZoneProperties | null
	}
}

const ZoneDraftContext = createContext<ZoneDraftContextType | undefined>(
	undefined
)

interface ZoneDraftProviderProps {
	children: ReactNode
	initialZone: Zone | null
}

export const ZoneDraftProvider: React.FC<ZoneDraftProviderProps> = ({
	children,
	initialZone,
}) => {
	const initialGroups = initialZone?.groups || []
	const initialProperties = initialZone
		? {
				name: initialZone.name,
				storeId: initialZone.storedId,
				flags: initialZone.flags || [],
			}
		: null

	const [state, setState] = useState<ZoneDraftState>({
		originalGroups: initialGroups,
		currentGroups: initialGroups,
		addedGroups: [],
		removedGroups: [],
		originalProperties: initialProperties,
		currentProperties: initialProperties,
		hasChanges: false,
		isSaving: false,
	})

	useEffect(() => {
		// Keep draft in sync with latest zone payload while there are no local edits.
		// This prevents stale nested group data (e.g. playlists/schedules) after reload.
		if (initialZone && !state.hasChanges) {
			const properties = {
				name: initialZone.name,
				storeId: initialZone.storedId,
				flags: initialZone.flags || [],
			}
			setState({
				originalGroups: initialZone.groups || [],
				currentGroups: initialZone.groups || [],
				addedGroups: [],
				removedGroups: [],
				originalProperties: properties,
				currentProperties: properties,
				hasChanges: false,
				isSaving: false,
			})
		}
	}, [initialZone, state.originalGroups, state.hasChanges])

	const checkHasChanges = useCallback(
		(
			addedGroups: number[],
			removedGroups: number[],
			originalProps: ZoneProperties | null,
			currentProps: ZoneProperties | null
		): boolean => {
			const groupsChanged = addedGroups.length > 0 || removedGroups.length > 0
			const flagsEqual = (a: string[], b: string[]) => {
				if (a.length !== b.length) return false
				const setA = new Set(a)
				return b.every((x) => setA.has(x))
			}
			const propsChanged = !!(
				originalProps &&
				currentProps &&
				(originalProps.name !== currentProps.name ||
					originalProps.storeId !== currentProps.storeId ||
					!flagsEqual(originalProps.flags, currentProps.flags))
			)
			return groupsChanged || propsChanged
		},
		[]
	)

	const setGroups = useCallback(
		(groups: Group[]) => {
			setState((prev) => {
				const original = prev.originalGroups || []
				const originalIds = original.map((g) => g.id)
				const currentIds = groups.map((g) => g.id)

				const added = currentIds.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds.includes(id))
				const hasChanges = checkHasChanges(
					added,
					removed,
					prev.originalProperties,
					prev.currentProperties
				)

				return {
					...prev,
					currentGroups: groups,
					addedGroups: added,
					removedGroups: removed,
					hasChanges,
				}
			})
		},
		[checkHasChanges]
	)

	const addGroups = useCallback(
		(groupIds: number[], allGroups: Group[]) => {
			setState((prev) => {
				const currentIds = prev.currentGroups.map((g) => g.id)
				const newGroupIds = groupIds.filter((id) => !currentIds.includes(id))

				const newGroups = allGroups.filter((g) => newGroupIds.includes(g.id))
				const updatedCurrent = [...prev.currentGroups, ...newGroups]

				const originalIds = (prev.originalGroups || []).map((g) => g.id)
				const currentIds2 = updatedCurrent.map((g) => g.id)

				const added = currentIds2.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds2.includes(id))
				const hasChanges = checkHasChanges(
					added,
					removed,
					prev.originalProperties,
					prev.currentProperties
				)

				return {
					...prev,
					currentGroups: updatedCurrent,
					addedGroups: added,
					removedGroups: removed,
					hasChanges,
				}
			})
		},
		[checkHasChanges]
	)

	const removeGroups = useCallback(
		(groupIds: number[]) => {
			setState((prev) => {
				const updatedCurrent = prev.currentGroups.filter(
					(g) => !groupIds.includes(g.id)
				)

				const originalIds = (prev.originalGroups || []).map((g) => g.id)
				const currentIds = updatedCurrent.map((g) => g.id)

				const added = currentIds.filter((id) => !originalIds.includes(id))
				const removed = originalIds.filter((id) => !currentIds.includes(id))
				const hasChanges = checkHasChanges(
					added,
					removed,
					prev.originalProperties,
					prev.currentProperties
				)

				return {
					...prev,
					currentGroups: updatedCurrent,
					addedGroups: added,
					removedGroups: removed,
					hasChanges,
				}
			})
		},
		[checkHasChanges]
	)

	const updateZoneProperties = useCallback(
		(properties: Partial<ZoneProperties>) => {
			setState((prev) => {
				const updated = {
					...prev.currentProperties,
					...properties,
				} as ZoneProperties
				if (updated.flags === undefined && prev.currentProperties) {
					updated.flags = prev.currentProperties.flags
				}
				if (updated.flags === undefined) {
					updated.flags = []
				}

				const hasChanges = checkHasChanges(
					prev.addedGroups,
					prev.removedGroups,
					prev.originalProperties,
					updated
				)

				return {
					...prev,
					currentProperties: updated,
					hasChanges,
				}
			})
		},
		[checkHasChanges]
	)

	const resetDraft = useCallback(() => {
		setState((prev) => ({
			originalGroups: prev.originalGroups,
			currentGroups: prev.originalGroups || [],
			addedGroups: [],
			removedGroups: [],
			originalProperties: prev.originalProperties,
			currentProperties: prev.originalProperties,
			hasChanges: false,
			isSaving: false,
		}))
	}, [])

	const updateWithFreshData = useCallback((zone: Zone) => {
		const properties = {
			name: zone.name,
			storeId: zone.storedId,
			flags: zone.flags || [],
		}
		setState({
			originalGroups: zone.groups || [],
			currentGroups: zone.groups || [],
			addedGroups: [],
			removedGroups: [],
			originalProperties: properties,
			currentProperties: properties,
			hasChanges: false,
			isSaving: false,
		})
	}, [])

	const setSaving = useCallback((isSaving: boolean) => {
		setState((prev) => ({ ...prev, isSaving }))
	}, [])

	const getChanges = useCallback(() => {
		const flagsEqual = (a: string[], b: string[]) => {
			if (a.length !== b.length) return false
			const setA = new Set(a)
			return b.every((x) => setA.has(x))
		}
		const propertiesChanged = !!(
			state.originalProperties &&
			state.currentProperties &&
			(state.originalProperties.name !== state.currentProperties.name ||
				state.originalProperties.storeId !== state.currentProperties.storeId ||
				!flagsEqual(
					state.originalProperties.flags,
					state.currentProperties.flags
				))
		)

		return {
			addedGroups: state.addedGroups,
			removedGroups: state.removedGroups,
			propertiesChanged,
			properties: propertiesChanged ? state.currentProperties : null,
		}
	}, [
		state.addedGroups,
		state.removedGroups,
		state.originalProperties,
		state.currentProperties,
	])

	const contextValue = useMemo(
		() => ({
			...state,
			current: state.currentGroups,
			setGroups,
			addGroups,
			removeGroups,
			updateZoneProperties,
			resetDraft,
			updateWithFreshData,
			setSaving,
			getChanges,
		}),
		[
			state,
			setGroups,
			addGroups,
			removeGroups,
			updateZoneProperties,
			resetDraft,
			updateWithFreshData,
			setSaving,
			getChanges,
		]
	)

	return (
		<ZoneDraftContext.Provider value={contextValue}>
			{children}
		</ZoneDraftContext.Provider>
	)
}

export const useZoneDraft = (): ZoneDraftContextType => {
	const context = useContext(ZoneDraftContext)
	if (!context) {
		throw new Error('useZoneDraft must be used within a ZoneDraftProvider')
	}
	return context
}
